import { Sentry } from '@formue-app/core';

import { loadOrCreateDpopKeyPair } from './key';
import { computeThumbprint, createDpopProof, exportPublicJwk } from './proof';

/**
 * The web app's DPoP signer — the object core's `setDpopSigner` takes:
 * `{ getThumbprint(), createProof({ htm, htu, accessToken, nonce }) }`. Core
 * decides what goes into a proof (`ath` for a presented token, the `nonce` a
 * server asked for); this only holds the key and signs.
 *
 * Constructing one is cheap and synchronous: nothing touches IndexedDB until
 * something asks for a thumbprint or a proof, so flag-off, legacy and mobile
 * users never open the database. The first call loads (or creates) the key
 * pair through one memoised promise, and **a failed load is memoised exactly
 * like a successful one** — no retry for the page's lifetime, so a browser
 * without a usable key store sends plain bearer tokens instead of hammering
 * IndexedDB on every request.
 *
 * "No key" is reported to Sentry once per page load (one breadcrumb plus one
 * info-level message, tagged with the reason) so the bearer-fallback rate is
 * observable. Reporting lives here, not in ./key.js, because the memoisation
 * is what guarantees it fires once.
 */
export const createDpopSigner = ({
  indexedDB = window.indexedDB,
  subtle = window.crypto?.subtle,
  sentry = Sentry,
  timeoutMs,
} = {}) => {
  let loaded = null;

  const reportUnavailable = (reason) => {
    sentry.addBreadcrumb({
      category: 'auth',
      level: 'warning',
      message: 'DPoP key unavailable',
      data: { reason },
    });
    sentry.captureMessage('DPoP key unavailable', {
      level: 'info',
      tags: { internal_system: 'authentication', dpop_key_reason: reason },
      extra: { reason },
    });
  };

  const unavailable = (reason) => {
    reportUnavailable(reason);
    return { keyPair: null, publicJwk: null, thumbprint: null, reason };
  };

  const load = () => {
    if (!loaded) {
      loaded = (async () => {
        try {
          const { keyPair, reason } = await loadOrCreateDpopKeyPair({
            indexedDB,
            subtle,
            timeoutMs,
          });

          if (!keyPair) {
            return unavailable(reason);
          }

          const publicJwk = await exportPublicJwk(subtle, keyPair.publicKey);
          const thumbprint = await computeThumbprint(subtle, publicJwk);
          return { keyPair, publicJwk, thumbprint, reason: null };
        } catch {
          // Nothing above rejects by design; if something ever does it is
          // still just "no key" to the caller, and never a rejected
          // getThumbprint.
          return unavailable('unknown');
        }
      })();
    }
    return loaded;
  };

  return {
    getThumbprint: async () => (await load()).thumbprint,

    createProof: async ({ htm, htu, accessToken, nonce }) => {
      const { keyPair, publicJwk } = await load();
      if (!keyPair) {
        // An internal invariant: core only asks for a proof once a thumbprint
        // has told it there is a key.
        throw new Error('DPoP key unavailable');
      }

      return createDpopProof(subtle, {
        privateKey: keyPair.privateKey,
        publicJwk,
        htm,
        htu,
        accessToken,
        nonce,
      });
    },
  };
};
